import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { ArrowLeftIcon, ArrowRightIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import {
	Button,
	Container,
	PAGE_CONTENT_WIDTH,
	Paragraph,
	Skeleton,
	tokens,
} from '@treams/treams-ui';

import GoalLearningSuggestionCard, {
	GoalLearningSuggestionCardSkeleton,
} from './GoalLearningSuggestionCard';
import useGoalLearningRecommendations from './useGoalLearningRecommendations';

const SUGGESTION_PAGE_SIZE = 3;
const MAX_EXTRA_PAGES = 2;

type Recommendation =
	graphql.GoalLearningRecommendationsQuery['goalLearningRecommendations']['recommendations'][number];

const Backdrop = styled(Box)`
	background-color: ${tokens.surface.secondary};
`;

export default function GoalLearningSuggestions(props: {
	title: string;
	description?: string;
	addedLearningCourseIDs: string[];
	onAdd: (course: graphql.LearningCourseFragment) => void;
}) {
	const { available, loading, recommendations } = useGoalLearningRecommendations({
		title: props.title,
		description: props.description,
	});

	if (!available) {
		return null;
	}

	const showSkeletons = loading && recommendations.length === 0;

	if (!showSkeletons && recommendations.length === 0) {
		return null;
	}

	const recommendationIDs = recommendations
		.map((recommendation) => recommendation.learningCourse.id)
		.join(',');

	// Remount on a new set of recommendations so pagination starts at the first page again.
	return (
		<GoalLearningSuggestionList
			key={recommendationIDs}
			showSkeletons={showSkeletons}
			recommendations={recommendations}
			addedLearningCourseIDs={props.addedLearningCourseIDs}
			onAdd={props.onAdd}
		/>
	);
}

function GoalLearningSuggestionList(props: {
	showSkeletons: boolean;
	recommendations: Recommendation[];
	addedLearningCourseIDs: string[];
	onAdd: (course: graphql.LearningCourseFragment) => void;
}) {
	const { t } = useTranslation();
	const [page, setPage] = useState(0);

	const maxPage = Math.min(
		MAX_EXTRA_PAGES,
		Math.max(0, Math.ceil(props.recommendations.length / SUGGESTION_PAGE_SIZE) - 1),
	);
	const visibleRecommendations = props.recommendations.slice(
		page * SUGGESTION_PAGE_SIZE,
		page * SUGGESTION_PAGE_SIZE + SUGGESTION_PAGE_SIZE,
	);
	const hasPreviousPage = page > 0;
	const hasNextPage = page < maxPage;

	return (
		<Backdrop pt={4}>
			<Container wide={PAGE_CONTENT_WIDTH}>
				<Flex alignItems="center" justifyContent="space-between" mb={2}>
					<Paragraph>{t('goals.edit.learningSuggestions.title')}</Paragraph>
					<Flex>
						<Box mr={2}>
							<Button
								type="transparent"
								small
								icon={<ArrowLeftIcon />}
								disabled={!hasPreviousPage}
								disableSuccess
								onClick={() => {
									setPage((current) => current - 1);
								}}
							/>
						</Box>
						<Button
							type="transparent"
							small
							rightIcon={<ArrowRightIcon />}
							title={t('goals.edit.learningSuggestions.viewMore')}
							disabled={!hasNextPage}
							disableSuccess
							onClick={() => {
								setPage((current) => current + 1);
							}}
						/>
					</Flex>
				</Flex>
				<Flex style={{ gap: 12 }}>
					{props.showSkeletons ? (
						<Skeleton.Repeat count={SUGGESTION_PAGE_SIZE}>
							<GoalLearningSuggestionCardSkeleton />
						</Skeleton.Repeat>
					) : (
						visibleRecommendations.map((recommendation) => (
							<GoalLearningSuggestionCard
								key={recommendation.learningCourse.id}
								course={recommendation.learningCourse}
								reason={recommendation.reason}
								added={props.addedLearningCourseIDs.includes(recommendation.learningCourse.id)}
								onAdd={props.onAdd}
							/>
						))
					)}
				</Flex>
			</Container>
		</Backdrop>
	);
}
